import { useQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { useFileSystemDuplexer } from './use-file-system-duplexer';
import { queryClient, queryKeys } from './query-client';

// Client data layer for the Your Agents page. One fetch to /api/my-agents (the symphony-bff
// ListMyAgents aggregation) returns the caller's agent team + team-wide skills. orion-web's CLIENT
// bundle must not import common-lib, so the shape lives here as a client-local contract matching
// what the route returns.


// The accent palette for the roster's SKILL chips (the design's mosaic hues).
// AGENTS are no longer colored from here — an agent's identity hue comes from the
// shared `colorForAgent(id)` rule (common-lib) so the same agent is the same color
// on every surface, stable across rename/reorder. This palette is skills-only.
// Drawn from the Accents token group — the design system's mosaic hues.
export const AGENT_HUES = [
  'var(--ds-accent-05)',
  'var(--ds-accent-07)',
  'var(--ds-accent-03)',
  'var(--ds-accent-08)',
  'var(--ds-accent-12)',
  'var(--ds-accent-10)',
];

export interface AgentSummary {
  id: string;
  name: string;
  title: string;
  description: string;
  // The agent's free-form persona/context prose. Forwarded from the BFF's AgentSummary
  // (the one adjacent per-agent field) so the detail screen's "Agent Context" card has real
  // content; empty when the agent has none.
  personality: string;
}

export interface SkillSummary {
  name: string;
  hint: string;
  // "SKILL_SOURCE_USER" | "SKILL_SOURCE_GENOME" (absent/other → treat as USER).
  // A skill is GENOME iff source === "SKILL_SOURCE_GENOME".
  source?: string;
}

export interface MyAgents {
  agents: AgentSummary[];
  skills: SkillSummary[];
  newSkills: number;
}

export type MyAgentsState =
  | { status: 'loading' }
  | { status: 'ready'; team: MyAgents }
  | { status: 'error' };

export async function fetchMyAgents(): Promise<MyAgents> {
  const res = await apiFetch(`${API_BASE}/api/my-agents`, { credentials: 'include' });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }
  const data = (await res.json()) as Partial<MyAgents>;
  return { agents: data.agents ?? [], skills: data.skills ?? [], newSkills: data.newSkills ?? 0 };
}

// Rename a team agent — optimistic, mirroring `lib/workspaces.ts` renameWorkspace and native's
// SymphonyStore.renameAgent: patch the cached name immediately, POST to /api/agent-rename, reconcile
// from the server echo, and roll the cache back on failure. The Agent Detail hero reads its name out
// of this same `queryKeys.myAgents` cache, so the optimistic patch updates it with no extra plumbing.
export async function renameAgent(id: string, name: string): Promise<void> {
  const prev = queryClient.getQueryData<MyAgents>(queryKeys.myAgents);
  if (prev) {
    queryClient.setQueryData<MyAgents>(queryKeys.myAgents, {
      ...prev,
      agents: prev.agents.map((a) => (a.id === id ? { ...a, name } : a)),
    });
  }
  try {
    const res = await apiFetch(`${API_BASE}/api/agent-rename`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ agentId: id, name }),
    });
    if (!res.ok) {
      // In `next dev` /api/* is proxied to the DEPLOYED pod (dev-proxy.mjs), so a brand-new route
      // 404s until it ships; a deployed run surfaces the backend's real error here instead.
      const detail = await res.text().catch(() => '');
      console.error(`[renameAgent] failed: HTTP ${res.status}`, detail.slice(0, 500));
      throw new Error(`HTTP ${res.status}`);
    }
    // Reconcile from the write's ECHO, not a full list refetch: /api/my-agents is the symphony-bff
    // aggregation, a different read path that can still be serving the old name right after an
    // AgentService write — refetching it here would clobber the rename straight back. The echo is
    // the authoritative post-write name (this is what native's updateAgentName reconciles from too).
    const body = (await res.json().catch(() => null)) as { agent?: { id?: string; name?: string } } | null;
    const confirmed = body?.agent?.name;
    if (confirmed) {
      queryClient.setQueryData<MyAgents>(queryKeys.myAgents, (cur) =>
        cur ? { ...cur, agents: cur.agents.map((a) => (a.id === id ? { ...a, name: confirmed } : a)) } : cur,
      );
    }
  } catch (err) {
    if (prev) queryClient.setQueryData<MyAgents>(queryKeys.myAgents, prev); // roll back
    throw err;
  }
}

// React Query's cache lets a route change reuse the last team instead of refetching; the
// file-system duplexer refreshes it when the agents' world changes.
export function useMyAgents(): MyAgentsState {
  const query = useQuery({ queryKey: queryKeys.myAgents, queryFn: fetchMyAgents });

  useFileSystemDuplexer(() => void query.refetch());

  // Keep a prior good team on a transient failure; only surface an error before the first success.
  if (query.data) return { status: 'ready', team: query.data };
  if (query.status === 'error') return { status: 'error' };
  return { status: 'loading' };
}
